import React, { useCallback, useMemo, useState } from "react";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  TextField as MuiTextField,
  Typography,
} from "@material-ui/core";
import { useDropzone } from "react-dropzone";
import {
  ArrayField,
  Button,
  Datagrid,
  FunctionField,
  TextField,
  useNotify,
} from "react-admin";

import {
  AddAllIcon,
  AttachFileIcon,
  CancelIcon,
  CloseIcon,
} from "../../../components/common/iconography";
import { MAX_FILE_SIZE, MAX_FILE_SIZE_MSG } from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";
import {
  ADDRESS_IMPORT_FIELDS,
  COUNTRY_DEFAULT,
  guessFieldMap,
  missingRequiredMappings,
  parseAddressCSV,
  planAddressImport,
} from "./addressImportUtils";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    justifyContent: "space-between",
  },
  title: {
    paddingTop: theme.spacing(2),
  },
  dialogContent: {
    height: "90vh",
  },
  dropZone: {
    background: theme.palette.background.default,
    cursor: "pointer",
    padding: theme.spacing(2),
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    textAlign: "center",
    color: theme.palette.text.secondary,
    transition: "background 0.2s ease-in-out",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  activeDropZone: {
    background: theme.palette.action.selected,
  },
  caption: {
    marginTop: theme.spacing(1),
  },
  fakeFieldset: {
    position: "relative",
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  fakeLegend: {
    position: "absolute",
    top: -theme.spacing(2),
    left: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    padding: "0 4px",
  },
  scrollableGrid: {
    overflowX: "auto",
    width: "100%",
    "&::-webkit-scrollbar": {
      height: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
  },
}));

/**
 * Upload a CSV of addresses, match its columns to the address fields, and
 * review what will be saved before anything is written.
 *
 * Saved addresses print on bills of lading, so the dialog's job is to make the
 * skipped rows visible first: duplicates of the account's existing entries,
 * duplicates within the file, partial addresses, and the values the address
 * form itself would refuse. The manager corrects the spreadsheet and re-uploads
 * rather than the import guessing on their behalf.
 *
 * The account's existing addresses arrive as a prop — the address book has
 * already loaded them, so this never re-fetches and the duplicate column always
 * agrees with the grid behind the dialog.
 */
export const AddressImportDialog = ({
  isOpen,
  setIsOpen,
  existingAddresses,
  handleImport,
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [csvData, setCSVData] = useState([]);
  const [csvErrors, setCSVErrors] = useState([]);
  const [columns, setColumns] = useState([]);
  const [addressFile, setAddressFile] = useState(null);
  const [fieldMap, setFieldMap] = useState({});

  // Every row is checked against the account's whole address book and against
  // the rows already accepted from this file, so a large upload costs a pass
  // over both. Planning it in the render body would spend that on every
  // keystroke, dialog state change and Datagrid update; it depends on nothing
  // else, so it is planned once per file, mapping or address-book change.
  const { decisions, importable, skipped } = useMemo(
    () => planAddressImport(csvData, fieldMap, existingAddresses),
    [csvData, fieldMap, existingAddresses],
  );

  // The preview renders one Status cell per row, and each scanning the whole
  // decision list for its own verdict would be the file's row count squared.
  // Indexed once per plan instead.
  const reasonByRowIndex = useMemo(
    () =>
      new Map(
        decisions.map((decision) => [decision.rowIndex, decision.reason]),
      ),
    [decisions],
  );

  const mappingErrors =
    csvData.length > 0 ? missingRequiredMappings(fieldMap) : [];

  /** Forget the loaded file entirely — its name included. Anything less leaves
   * the dialog describing a file it is not holding: a "Selected: …" line above
   * an empty preview reads as a file that loaded, and rows left from an earlier
   * upload would be reviewed, and imported, under the new file's name. */
  const clearLoadedFile = () => {
    setAddressFile(null);
    setCSVData([]);
    setCSVErrors([]);
    setColumns([]);
    setFieldMap({});
  };

  const handleCSVFile = async (file) => {
    try {
      clearLoadedFile();
      if (!file) return;
      if (file.size > MAX_FILE_SIZE) {
        notify(MAX_FILE_SIZE_MSG, "warning");
        return;
      }

      setLoading(true);
      setAddressFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const parsed = parseAddressCSV(event.target.result);
          setCSVErrors(parsed.errors);
          setCSVData(parsed.rows);
          setColumns(parsed.columns);
          setFieldMap(guessFieldMap(parsed.columns));
        } catch (err) {
          logError("accounts:AddressImportDialog:parse", err, notify);
          clearLoadedFile();
        } finally {
          setLoading(false);
        }
      };
      reader.onerror = () => {
        setLoading(false);
        clearLoadedFile();
        notify("Could not read that file.", "warning");
      };
      reader.readAsText(file);
    } catch (err) {
      logError("accounts:AddressImportDialog:handleCSVFile", err, notify);
      setLoading(false);
      clearLoadedFile();
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) handleCSVFile(acceptedFiles[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ".csv",
    multiple: false,
    onDrop,
  });

  const handleClose = () => {
    clearLoadedFile();
    setIsOpen(false);
  };

  const handleSubmit = () => {
    // The skipped count goes with the rows: the review panel that showed it is
    // gone once the dialog closes, so the notification is the only place left
    // that can say the file was not imported whole.
    handleImport(importable, skipped.length);
    handleClose();
  };

  return (
    <Dialog maxWidth="xl" open={isOpen} fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography className={classes.title}>Address Book Upload</Typography>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Grid container direction="row" spacing={2}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="overline" gutterBottom>
                Instructions
              </Typography>
              <ol>
                <li>
                  Include the business name, street address, city, state and zip
                  code; country and phone are optional.
                </li>
                <li>
                  Save as a comma-separated-values (.CSV) file and upload here.
                </li>
                <li>
                  Match the spreadsheet columns to the address fields, then
                  review the rows below before importing.
                </li>
                <li>
                  States spelled out in full ("Texas") are converted to their
                  2-letter code. A blank country is saved as {COUNTRY_DEFAULT}.
                </li>
                <li>For help, contact us at 877-TRANSLOAD / 877-872-6756</li>
              </ol>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="overline" gutterBottom>
                Upload Address CSV
              </Typography>
              <Box
                {...getRootProps()}
                className={`${classes.dropZone} ${
                  isDragActive ? classes.activeDropZone : ""
                }`}
              >
                <input {...getInputProps()} />
                <AttachFileIcon fontSize="large" />
                <Typography variant="body2" color="textSecondary">
                  {isDragActive
                    ? "Drop your CSV file here..."
                    : "Drag & drop a CSV file here, or click to select one"}
                </Typography>
              </Box>
              {addressFile?.name && (
                <Typography variant="caption" className={classes.caption}>
                  Selected: {addressFile.name}
                </Typography>
              )}
            </Grid>
          </Grid>

          {(csvErrors.length > 0 || mappingErrors.length > 0) && (
            <Grid item xs={12}>
              <Typography variant="overline" color="error">
                Loading Errors:
              </Typography>
              <ul>
                {[...csvErrors, ...mappingErrors].map((err, idx) => (
                  <Typography key={idx} color="error">
                    <li>{err}</li>
                  </Typography>
                ))}
              </ul>
            </Grid>
          )}

          {columns.length > 0 && (
            <>
              <Grid item xs={12} className={classes.fakeFieldset}>
                <div id="field-map-legend" className={classes.fakeLegend}>
                  <Typography variant="overline" color="textSecondary">
                    Match Spreadsheet columns to fields
                  </Typography>
                </div>
                <Grid container spacing={1}>
                  {ADDRESS_IMPORT_FIELDS.map((field) => (
                    <Grid key={field.id} item xs={12} md={3}>
                      <MuiTextField
                        select
                        id={`select_${field.id}`}
                        size="small"
                        margin="dense"
                        variant="outlined"
                        label={field.name}
                        fullWidth
                        value={fieldMap?.[field.id] ?? ""}
                        onChange={(e) =>
                          setFieldMap({
                            ...fieldMap,
                            [field.id]: e.target.value,
                          })
                        }
                        required={field.required}
                        helperText={false}
                      >
                        <MenuItem value="">
                          <span>&nbsp;</span>
                        </MenuItem>
                        {columns.map((col) => (
                          <MenuItem
                            key={`${field.id}_${col.id}`}
                            value={col.id}
                          >
                            {col.name}
                          </MenuItem>
                        ))}
                      </MuiTextField>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12} className={classes.fakeFieldset}>
                <div id="import-preview-legend" className={classes.fakeLegend}>
                  <Typography variant="overline" color="textSecondary">
                    {`Review rows — ${importable.length} to import, ${skipped.length} skipped`}
                  </Typography>
                </div>
                <div className={classes.scrollableGrid}>
                  <ArrayField source="preview" record={{ preview: csvData }}>
                    <Datagrid rowClick={false} bulkActionButtons={false}>
                      <FunctionField
                        label="Status"
                        render={(record) => {
                          // The plan already decided this row — read its verdict
                          // rather than deciding again, so the grid and the
                          // counts above it can never disagree.
                          const reason = reasonByRowIndex.get(record.rowIndex);
                          return (
                            <Typography
                              variant="body2"
                              color={reason ? "error" : "textPrimary"}
                            >
                              {reason ? `Skipped — ${reason}` : "Import"}
                            </Typography>
                          );
                        }}
                      />
                      {columns.map((col) => (
                        <TextField
                          key={col.id}
                          source={col.id}
                          label={col.name}
                        />
                      ))}
                    </Datagrid>
                  </ArrayField>
                </div>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button label="Cancel" color="default" autoFocus onClick={handleClose}>
          <CancelIcon />
        </Button>
        <Button
          label="IMPORT"
          color="primary"
          // A file-level parse error blocks the import as firmly as a missing
          // mapping does: a row with an unquoted comma has every field after it
          // shifted a column left, and a shifted row can still look complete —
          // it saves an address that prints on a bill of lading with the city
          // in the state column. The manager fixes the spreadsheet and
          // re-uploads.
          disabled={
            loading ||
            csvErrors.length > 0 ||
            mappingErrors.length > 0 ||
            importable.length === 0
          }
          onClick={handleSubmit}
        >
          <AddAllIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
